/**
 * Pure decisions behind the failsafe tab, kept out of the component so they can
 * be tested without a DOM (this repo has no component-render harness -- see
 * client.test.ts for the logic-layer convention).
 */

import type {
  FailsafeApplyResult,
  FailsafeDoctrine,
  FleetFailsafeStatus,
} from '@/types/domain';

/**
 * A doctrine that disables the autopilot's return-to-operator failsafes must be
 * acknowledged before it is sent. This is the same predicate the server guards
 * with (400 without confirm=true); the UI mirrors it so the confirm card
 * appears before the call, not after the rejection.
 */
export function doctrineNeedsConfirm(doctrine: FailsafeDoctrine): boolean {
  return doctrine.disablesSafety;
}

export interface ApplySummary {
  ok: boolean;
  line: string;
}

/**
 * Collapse a per-parameter apply result into one status line for the tab
 * header. A partial apply is NOT a success: apply_doctrine stops at the first
 * parameter the aircraft would not take, so some FS_* were written and the
 * rest were not -- the aircraft is in a mixed state the operator must see, so
 * the line names the parameter that stopped it rather than a bare "failed".
 */
export function summarizeApply(result: FailsafeApplyResult): ApplySummary {
  if (result.ok) {
    const n = result.applied.length;
    return { ok: true, line: `${n} parameter${n === 1 ? '' : 's'} set` };
  }
  const failed = result.failed;
  const stopped = failed ? `${failed.name} — ${failed.message}` : 'apply failed';
  const setCount = result.applied.length;
  return {
    ok: false,
    line: `Stopped at ${stopped}${setCount ? ` (${setCount} already set)` : ''}`,
  };
}

export interface FleetStatusStyle {
  label: string;
  tone: string;
}

/**
 * Map a fleet per-drone status to a label and colour. The five outcomes are
 * three visually distinct classes, deliberately: `failed` is the only one that
 * means something went wrong on the wire (danger); `skipped-armed` is a
 * pre-flight refusal the operator chose the timing of (caution); `no-link` and
 * `no-access` are simply not-attempted (muted). Collapsing them to a binary
 * ok/failed -- as OutcomeList does -- would read an armed aircraft or one out
 * of scope as a failure it is not.
 */
export function fleetStatusStyle(status: FleetFailsafeStatus): FleetStatusStyle {
  switch (status) {
    case 'applied':
      return { label: 'APPLIED', tone: 'var(--ok)' };
    case 'skipped-armed':
      return { label: 'ARMED — SKIPPED', tone: 'var(--caution)' };
    case 'no-link':
      return { label: 'NO LINK', tone: 'var(--muted-foreground-3)' };
    case 'no-access':
      return { label: 'NO ACCESS', tone: 'var(--muted-foreground-3)' };
    case 'failed':
      return { label: 'FAILED', tone: 'var(--danger-foreground)' };
  }
}

/**
 * The one-line "active doctrine" summary for the tab header. `matched` names
 * the doctrine; `custom` says the aircraft runs a configuration matching none
 * of ours; `unavailable` keeps the honest read-back-not-available note (the
 * aircraft could not be read).
 */
export function currentLine(
  current: string | null,
  currentDetail: string,
  available: FailsafeDoctrine[],
): string {
  if (currentDetail === 'matched' && current) {
    const doctrine = available.find((d) => d.key === current);
    return `Active: ${doctrine ? doctrine.label : current}`;
  }
  if (currentDetail === 'custom') {
    return 'Active: custom configuration (matches no preset).';
  }
  return 'Current doctrine unknown — read-back not available.';
}
